/**
 * Admin Entry Detail Dialog
 *
 * Dialog showing transaction details for admin review with feedback capabilities.
 * Displays original vs current narrative when the attorney has made edits.
 * Includes carrier prediction toggle (approved/rejected) and feedback reason.
 * Supports navigation between entries with auto-save on navigate.
 */

import { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AdminTransaction } from "../hooks/useAdminData";
import FeedbackSection from "./FeedbackSection";
import { PredictionValue } from "@/shared/components/PredictionToggle";

/** Payload sent to backend when saving feedback */
export interface FeedbackPayload {
  entryId: number;
  approved: boolean;
  needsImprovement: boolean;
  /** Rejection reason - null if not rejected, string value if rejected */
  deducted: string | null;
  attorneyFeedback: string | null;
  hourglassFeedback: string | null;
}

interface AdminEntryDetailDialogProps {
  entry: AdminTransaction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when user clicks previous/next navigation */
  onNavigate?: (direction: "previous" | "next") => void;
  /** Whether there's a previous entry to navigate to */
  hasPrevious?: boolean;
  /** Whether there's a next entry to navigate to */
  hasNext?: boolean;
  /** Called when saving feedback - returns promise for loading state */
  onSaveFeedback?: (payload: FeedbackPayload) => Promise<void>;
  /** Whether a save operation is in progress */
  isSaving?: boolean;
}

export default function AdminEntryDetailDialog({
  entry,
  open,
  onOpenChange,
  onNavigate,
  hasPrevious = false,
  hasNext = false,
  onSaveFeedback,
  isSaving = false,
}: AdminEntryDetailDialogProps) {
  // Local feedback form state
  const [prediction, setPrediction] = useState<PredictionValue>(null);
  const [attorneyFeedback, setAttorneyFeedback] = useState("");
  const [hourglassFeedback, setHourglassFeedback] = useState("");
  const [rejectionReason, setRejectionReason] = useState<string | null>(null);

  // Compute initial values from entry for change detection
  const initialPrediction = useMemo((): PredictionValue => {
    if (!entry) return null;
    if (entry.needs_improvement) return "needs_improvement";
    if (entry.approved) return "approved";
    if (entry.deducted) return "rejected"; // deducted is now string, truthy check works
    return null;
  }, [entry?.id, entry?.approved, entry?.needs_improvement, entry?.deducted]);

  const initialAttorneyFeedback = entry?.attorney_feedback || "";
  const initialHourglassFeedback = entry?.hourglass_feedback || "";
  const initialRejectionReason = entry?.deducted || null;

  // Detect if user has made changes
  const hasChanges =
    prediction !== initialPrediction ||
    attorneyFeedback !== initialAttorneyFeedback ||
    hourglassFeedback !== initialHourglassFeedback ||
    rejectionReason !== initialRejectionReason;

  // Sync local state when entry changes (navigation or initial open)
  // Include feedback fields in deps to sync on real-time updates (e.g., socket refresh)
  useEffect(() => {
    if (entry) {
      setPrediction(initialPrediction);
      setAttorneyFeedback(entry.attorney_feedback || "");
      setHourglassFeedback(entry.hourglass_feedback || "");
      setRejectionReason(entry.deducted || null);
    }
  }, [entry?.id, initialPrediction, entry?.attorney_feedback, entry?.hourglass_feedback, entry?.deducted]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setPrediction(null);
      setAttorneyFeedback("");
      setHourglassFeedback("");
      setRejectionReason(null);
    }
  }, [open]);

  if (!entry) return null;

  const hasNarrativeChange =
    entry.original_description !== null &&
    entry.original_description !== entry.description;

  const hasUnitsChange =
    entry.original_units !== null && entry.original_units !== entry.units;

  const hasMatterChange =
    entry.original_matter !== null && entry.original_matter !== entry.matter_id;

  const hasAnyChange = hasNarrativeChange || hasUnitsChange || hasMatterChange;

  // Derive entry status for badge display
  const entryStatus = entry.source === "deleted_transactions"
    ? "deleted"
    : entry.exported
      ? "exported"
      : "tracked";

  /** Format date from ISO string to readable format */
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  /** Save feedback to backend. Returns true if successful, false if failed. */
  const handleSave = async (): Promise<boolean> => {
    if (!entry || !onSaveFeedback) return false;

    // Validate: if rejected, must have rejection reason
    if (prediction === "rejected" && !rejectionReason?.trim()) {
      toast.error("Please select a rejection reason");
      return false;
    }

    try {
      await onSaveFeedback({
        entryId: entry.id,
        approved: prediction === "approved",
        needsImprovement: prediction === "needs_improvement",
        // Send rejection reason string if rejected, null otherwise
        deducted: prediction === "rejected" ? rejectionReason?.trim() || null : null,
        attorneyFeedback: attorneyFeedback.trim() || null,
        hourglassFeedback: hourglassFeedback.trim() || null,
      });
      return true;
    } catch {
      // onSaveFeedback already showed a toast, no need to show another
      return false;
    }
  };

  /** Handle navigation with auto-save if changes exist */
  const handleNavigate = async (direction: "previous" | "next") => {
    // Auto-save if there are unsaved changes
    if (hasChanges && onSaveFeedback && entry) {
      const saved = await handleSave();
      if (!saved) {
        // Don't navigate - user needs to fix the issue or manually discard changes
        return;
      }
    }
    onNavigate?.(direction);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold flex items-center gap-3">
            Entry Details
            {/* Status Badge - inline with title */}
            <span
              className={cn(
                "text-xs font-semibold px-2.5 py-1 rounded-full",
                entryStatus === "exported" &&
                  "bg-green-100 text-green-700 border border-green-500/50 dark:bg-green-950/30 dark:text-green-400 dark:border-border",
                entryStatus === "deleted" &&
                  "bg-red-100 text-red-700 border border-red-500/50 dark:bg-red-950/30 dark:text-red-400 dark:border-border",
                entryStatus === "tracked" &&
                  "bg-amber-100 text-amber-700 border border-amber-500/50 dark:bg-amber-950/30 dark:text-amber-400 dark:border-border"
              )}
            >
              {entryStatus === "exported" && "Exported"}
              {entryStatus === "deleted" && "Deleted"}
              {entryStatus === "tracked" && "Tracked"}
            </span>
            {hasAnyChange && (
              <span className="text-xs font-normal text-amber-600 dark:text-amber-400">
                (edited by attorney)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Document Name (Task Title) - if present */}
          {entry.document_name && entry.document_name.trim() !== "" && (
            <div className="grid w-full gap-1.5">
              <Label>Task Title</Label>
              <Input
                value={entry.document_name}
                className="col-span-3 cursor-default focus-visible:ring-0 focus-visible:ring-offset-0"
                readOnly
                tabIndex={-1}
              />
            </div>
          )}

          {/* Matter */}
          <div className={cn("grid w-full gap-1.5", hasMatterChange && "pb-5")}>
            <Label>Matter</Label>
            <div className="relative">
              <Input
                value={
                  entry.matter_number
                    ? `${entry.matter_number}${entry.matter_description ? ` | ${entry.matter_description}` : ""}`
                    : "—"
                }
                className="col-span-3 cursor-default focus-visible:ring-0 focus-visible:ring-offset-0"
                readOnly
                tabIndex={-1}
              />
              {hasMatterChange && (
                <p className="absolute left-0 top-full mt-1 text-sm text-amber-600 dark:text-amber-400">
                  Original matter: {entry.original_matter || "None"}
                </p>
              )}
            </div>
          </div>

          {/* Task, Activity, Date, Units - all on one row */}
          <div className={cn("grid gap-3 grid-cols-4", hasUnitsChange && "pb-5")}>
            <div className="grid w-full gap-1.5">
              <Label className="text-xs">Task</Label>
              <Input
                value={entry.task_code || "—"}
                className="cursor-default focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
                readOnly
                tabIndex={-1}
              />
            </div>
            <div className="grid w-full gap-1.5">
              <Label className="text-xs">Activity</Label>
              <Input
                value={entry.activity_code || "—"}
                className="cursor-default focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
                readOnly
                tabIndex={-1}
              />
            </div>
            <div className="grid w-full gap-1.5">
              <Label className="text-xs">Date</Label>
              <Input
                value={formatDate(entry.date)}
                className="cursor-default focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
                readOnly
                tabIndex={-1}
              />
            </div>
            <div className="grid w-full gap-1.5">
              <Label className="text-xs">Units</Label>
              <div className="relative">
                <Input
                  value={String(entry.units)}
                  className="cursor-default focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
                  readOnly
                  tabIndex={-1}
                />
                {hasUnitsChange && (
                  <p className="absolute left-0 top-full mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Original: {entry.original_units}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Narrative Section */}
          <div className="grid w-full gap-1.5">
            {hasNarrativeChange ? (
              <div className="space-y-3">
                {/* Original Narrative (AI Generated) */}
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <Label>Original Narrative</Label>
                    <span className="text-xs px-1.5 py-0.5 rounded border border-muted-foreground text-muted-foreground">
                      AI Generated
                    </span>
                  </div>
                  <div className="p-3 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-sm min-h-[80px]">
                    {entry.original_description}
                  </div>
                </div>

                {/* Current Narrative */}
                <div>
                  <Label className="mb-1.5 block">Current Narrative</Label>
                  <div className="p-3 rounded-md bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-sm min-h-[80px]">
                    {entry.description}
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <Label className="mb-1.5 block">Narrative</Label>
                <div className="p-3 rounded-md bg-muted/50 border text-sm min-h-[80px]">
                  {entry.description || "No narrative"}
                </div>
              </div>
            )}
          </div>

          {/* Feedback Section */}
          {onSaveFeedback && (
            <FeedbackSection
              prediction={prediction}
              attorneyFeedback={attorneyFeedback}
              onPredictionChange={setPrediction}
              onAttorneyFeedbackChange={setAttorneyFeedback}
              hourglassFeedback={hourglassFeedback}
              onHourglassFeedbackChange={setHourglassFeedback}
              rejectionReason={rejectionReason}
              onRejectionReasonChange={setRejectionReason}
              disabled={isSaving}
            />
          )}
        </div>

        {/* Footer with navigation and save */}
        {onSaveFeedback && (
          <DialogFooter className="sm:justify-between gap-2">
            {/* Previous button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleNavigate("previous")}
                    disabled={!hasPrevious || isSaving}
                    className="h-10 w-10"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Previous entry</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Save button */}
            <Button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="bg-[hsl(var(--brand-alt))] text-white hover:bg-[hsl(var(--brand-alt-hover))] border-[hsl(var(--brand-alt))]"
            >
              {isSaving ? "Saving..." : "Save Feedback"}
            </Button>

            {/* Next button */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleNavigate("next")}
                    disabled={!hasNext || isSaving}
                    className="h-10 w-10"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Next entry</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
